import {
    Chart,
    BarController, BarElement,
    DoughnutController, ArcElement,
    LineController, LineElement, PointElement,
    CategoryScale, LinearScale,
    Tooltip, Legend,
} from 'chart.js';

Chart.register(BarController, BarElement, DoughnutController, ArcElement, LineController, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

const COLOR = 'rgba(30, 100, 200, 0.75)';
const COLOR_HOVER = 'rgba(30, 100, 200, 1)';

const DONUT_COLORS = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
    '#06b6d4', '#f97316', '#84cc16', '#ec4899', '#6366f1',
    '#14b8a6', '#a855f7', '#0ea5e9', '#d946ef',
];

function makeBar(id, labels, data, labelText) {
    const canvas = document.getElementById(id);
    if (!canvas) return;

    new Chart(canvas, {
        type: 'bar',
        data: {
            labels,
            datasets: [{
                label: labelText,
                data,
                backgroundColor: COLOR,
                hoverBackgroundColor: COLOR_HOVER,
                borderRadius: 2,
            }],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: { mode: 'index' },
            },
            scales: {
                x: { grid: { display: false } },
                y: { beginAtZero: true, ticks: { precision: 0 } },
            },
        },
    });
}

function makeAutoRecognitionLine(id, d) {
    const canvas = document.getElementById(id);
    if (!canvas) return;

    new Chart(canvas, {
        type: 'line',
        data: {
            labels: d.labels,
            datasets: [{
                label: 'Automatisch erkannt (%)',
                data: d.percent,
                borderColor: COLOR_HOVER,
                backgroundColor: COLOR,
                pointRadius: 3,
                tension: 0.25,
                spanGaps: false,
            }],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (ctx) => {
                            const i = ctx.dataIndex;
                            const pct = ctx.parsed.y;
                            if (pct === null) return 'Keine Auto-Checks';
                            return `${pct}% (${d.passed[i]} von ${d.total[i]})`;
                        },
                    },
                },
            },
            scales: {
                x: { grid: { display: false } },
                y: {
                    beginAtZero: true,
                    max: 100,
                    ticks: { callback: (v) => `${v}%` },
                },
            },
        },
    });
}

function makeStackedBar(id, months, stores, matrix, yLabel, isFloat) {
    const canvas = document.getElementById(id);
    if (!canvas) return;

    const datasets = stores.map((store, idx) => ({
        label: store,
        data: matrix.map(row => row[idx]),
        backgroundColor: DONUT_COLORS[idx % DONUT_COLORS.length],
    }));

    new Chart(canvas, {
        type: 'bar',
        data: { labels: months, datasets },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 10 } } },
                tooltip: {
                    mode: 'index',
                    callbacks: isFloat ? {
                        label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(2)} €`,
                    } : undefined,
                },
            },
            scales: {
                x: { stacked: true, grid: { display: false } },
                y: {
                    stacked: true,
                    beginAtZero: true,
                    ticks: isFloat ? {} : { precision: 0 },
                    title: { display: !!yLabel, text: yLabel || '' },
                },
            },
        },
    });
}

function makeTable(containerId, months, stores, matrix, isFloat, fieldsearchUrl, dateFieldId) {
    const container = document.getElementById(containerId);
    if (!container || !months.length || !stores.length) return;

    const fmt = isFloat ? v => v.toFixed(2) + ' €' : v => String(v);

    const monthLabel = ym => {
        const [year, mon] = ym.split('-');
        return `${mon}/${year}`;
    };

    const monthLink = ym => {
        if (!fieldsearchUrl || !dateFieldId) return monthLabel(ym);
        const [year, mon] = ym.split('-');
        const param = encodeURIComponent(`.${mon}.${year}`);
        const url = `${fieldsearchUrl}?field_${dateFieldId}=${param}&all_sources=1`;
        return `<a href="${url}">${monthLabel(ym)}</a>`;
    };

    const colTotals = stores.map((_, ci) => matrix.reduce((sum, row) => sum + row[ci], 0));
    const grandTotal = colTotals.reduce((a, b) => a + b, 0);

    let html = '<table class="uk-table uk-table-divider uk-table-striped uk-table-small uk-text-small">';
    html += '<thead><tr><th>Monat</th>';
    stores.forEach(s => { html += `<th>${s}</th>`; });
    html += '<th><strong>Total</strong></th></tr></thead><tbody>';

    months.forEach((month, ri) => {
        const rowTotal = matrix[ri].reduce((a, b) => a + b, 0);
        html += `<tr><td>${monthLink(month)}</td>`;
        matrix[ri].forEach(v => { html += `<td>${fmt(v)}</td>`; });
        html += `<td><strong>${fmt(rowTotal)}</strong></td></tr>`;
    });

    html += '<tr><td><strong>Total</strong></td>';
    colTotals.forEach(t => { html += `<td><strong>${fmt(t)}</strong></td>`; });
    html += `<td><strong>${fmt(grandTotal)}</strong></td></tr>`;
    html += '</tbody></table>';

    container.innerHTML = html;
}

function makeDonut(id, labels, data) {
    const canvas = document.getElementById(id);
    if (!canvas) return;

    new Chart(canvas, {
        type: 'doughnut',
        data: {
            labels,
            datasets: [{
                data,
                backgroundColor: DONUT_COLORS.slice(0, data.length),
                borderWidth: 2,
                borderColor: '#fff',
            }],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            cutout: '65%',
            plugins: {
                legend: { display: false },
                tooltip: { mode: 'index' },
            },
        },
    });
}

document.addEventListener('DOMContentLoaded', () => {
    const el = document.getElementById('dashboard-data');
    if (!el) return;

    const d = JSON.parse(el.textContent);

    makeDonut('chart-states', d.stateDist.labels, d.stateDist.data);

    // Build legend
    const legend = document.getElementById('state-legend');
    if (legend) {
        legend.innerHTML = d.stateDist.labels.map((label, i) => {
            const color = DONUT_COLORS[i % DONUT_COLORS.length];
            return `<li><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};margin-right:6px"></span>${label}: <strong>${d.stateDist.data[i]}</strong></li>`;
        }).join('');
    }

    // Build end-state totals
    const totalsEl = document.getElementById('end-state-totals');
    if (totalsEl) {
        const rows = d.endStateTotals.map(t =>
            `<tr><td>${t.label}</td><td class="uk-text-right">${t.whatsapp}</td><td class="uk-text-right">${t.manual}</td><td class="uk-text-right uk-text-bold">${t.count}</td></tr>`
        ).join('');
        const sum = d.endStateTotals.reduce((acc, t) => {
            acc.whatsapp += t.whatsapp;
            acc.manual   += t.manual;
            acc.count    += t.count;
            return acc;
        }, { whatsapp: 0, manual: 0, count: 0 });
        totalsEl.innerHTML =
            `<table class="uk-table uk-table-divider uk-margin-remove">
                <thead>
                    <tr>
                        <th></th>
                        <th class="uk-text-right">WhatsApp</th>
                        <th class="uk-text-right">Manuell</th>
                        <th class="uk-text-right">Gesamt</th>
                    </tr>
                </thead>
                <tbody>${rows}</tbody>
                <tfoot>
                    <tr style="font-size: 1rem">
                        <td class="uk-text-bold">Gesamt</td>
                        <td class="uk-text-right uk-text-bold">${sum.whatsapp}</td>
                        <td class="uk-text-right uk-text-bold">${sum.manual}</td>
                        <td class="uk-text-right uk-text-bold">${sum.count}</td>
                    </tr>
                </tfoot>
            </table>`;
    }
    makeBar('chart-per-day',   d.perDay.labels,   d.perDay.data,   'Teilnehmer');
    if (d.autoRecognition) {
        makeAutoRecognitionLine('chart-auto-recognition', d.autoRecognition);
    }
    makeBar('chart-per-hour',  d.perHour.labels,  d.perHour.data,  'Teilnehmer');
    makeBar('chart-per-week',  d.perWeek.labels,  d.perWeek.data,  'Betrag (€)');
    makeBar('chart-per-store', d.perStore.labels, d.perStore.data, 'Teilnehmer');

    if (d.perStoreMonthCount) {
        makeStackedBar(
            'chart-store-month-count',
            d.perStoreMonthCount.months,
            d.perStoreMonthCount.stores,
            d.perStoreMonthCount.matrix,
            'Einlösungen',
            false,
        );
        makeTable(
            'table-store-month-count',
            d.perStoreMonthCount.months,
            d.perStoreMonthCount.stores,
            d.perStoreMonthCount.matrix,
            false,
            d.fieldsearchUrl,
            d.dateFieldId,
        );
    }
    if (d.perStoreMonthPayout) {
        makeStackedBar(
            'chart-store-month-payout',
            d.perStoreMonthPayout.months,
            d.perStoreMonthPayout.stores,
            d.perStoreMonthPayout.matrix,
            'Betrag (€)',
            true,
        );
        makeTable(
            'table-store-month-payout',
            d.perStoreMonthPayout.months,
            d.perStoreMonthPayout.stores,
            d.perStoreMonthPayout.matrix,
            true,
            d.fieldsearchUrl,
            d.dateFieldId,
        );
    }
});
